import { useRef, forwardRef, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { ListItemList } from '../styled-components'
import { ListItemsContext } from './list-items-context'

import {
  listItemListViewVariantOptions,
  listItemsCheckVariantOptions
} from '../constants'

import RadioComponent from '../../../atoms/radio'
import { radioVariantOptions } from '../../../atoms/radio/constants'

const ListItemsRoot = forwardRef(
  (
    {
      isForm = false,
      listViewType,
      dividerFull,
      hideDivider = false,
      checkComponent,
      children,
      ...props
    },
    fowardedRef
  ) => {
    // applying flat() to allow items from separated arrays.
    const flattenedChildren = Array.isArray(children)
      ? children.flat()
      : [children]

    const listViewTypeDefinition =
      flattenedChildren.length > 1
        ? listViewType
        : listItemListViewVariantOptions.SEPARATE

    const getItemKey = (item) => item?.props?.itemKey

    const firstItemKey =
      flattenedChildren.length > 0
        ? getItemKey(flattenedChildren[0])
        : undefined

    const lastItemKey =
      flattenedChildren.length > 0
        ? getItemKey(flattenedChildren[flattenedChildren.length - 1])
        : undefined

    const { checkInputType, radioDefaultValue, handleSelect } =
      checkComponent || {}

    const formRef = useRef()

    const handleSubmit = useCallback(
      (e) => {
        e.preventDefault()

        const form = new FormData(formRef.current)
        const dataForm = Object.fromEntries(form)

        handleSelect && handleSelect(dataForm)
      },
      [handleSelect]
    )

    const DefineRootIsForm = () => {
      return isForm ? <form ref={formRef}>{children}</form> : children
    }

    const contextValue = useMemo(
      () => ({
        listViewType: listViewTypeDefinition,
        dividerFull,
        hideDivider,
        firstItemKey,
        lastItemKey,
        checkComponent,
        handleSubmit
      }),
      [
        listViewTypeDefinition,
        dividerFull,
        hideDivider,
        firstItemKey,
        lastItemKey,
        checkComponent,
        handleSubmit
      ] // Dependências do useMemo
    )

    return (
      <ListItemsContext.Provider value={contextValue}>
        <ListItemList
          ref={fowardedRef}
          listViewVariant={listViewTypeDefinition}
          {...props}
        >
          {checkInputType === listItemsCheckVariantOptions.RADIO ? (
            <RadioComponent.Root
              defaultValue={radioDefaultValue}
              variant={radioVariantOptions.STANDARD}
            >
              {children}
            </RadioComponent.Root>
          ) : (
            <DefineRootIsForm />
          )}
        </ListItemList>
      </ListItemsContext.Provider>
    )
  }
)

ListItemsRoot.propTypes = {
  isForm: PropTypes.bool,
  listViewType: PropTypes.oneOf(Object.values(listItemListViewVariantOptions)),
  dividerFull: PropTypes.bool,
  hideDivider: PropTypes.bool,
  checkComponent: PropTypes.shape({
    checkInputType: PropTypes.oneOf(
      Object.values(listItemsCheckVariantOptions)
    ),
    radioDefaultValue: PropTypes.string,
    handleSelect: PropTypes.string
  }),
  children: PropTypes.node
}

ListItemsRoot.displayName = 'ListItemsRoot'

export default ListItemsRoot
